import { Table, Text } from '@factorialco/deprecated-design-system'
import {
  BookkeepersManagementValueObjectsIncidenceRuleValidityEnum,
  PayrollSendToBookkeeperIncidenceFragment,
} from 'generated/resources/graphql'

import usePagination from 'modules/core/hooks/usePagination'
import i18n from 'modules/core/lib/i18n'

import { IncidenceRow } from './_IncidenceRow'

interface Props {
  incidences?: PayrollSendToBookkeeperIncidenceFragment[]
}

const RuleEvaluationIncidenceTable = ({ incidences }: Props) => {
  // If we can't retrieve the employee data don't show the update
  const filteredIncidences = (incidences ?? [])
    .filter((incidence) => incidence.employee)
    .sort(sortIncidences)

  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: 50,
    items: filteredIncidences,
  })

  return (
    <Table autoLayout overflowY="auto" maxHeight="s200">
      <Table.Head>
        <Table.Row>
          <Table.HeadCell>
            <Text.Normal size="200" whiteSpace="nowrap" weight="semibold">
              {i18n.t('bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.table.type')}
            </Text.Normal>
          </Table.HeadCell>
          <Table.HeadCell>
            <Text.Normal size="200" whiteSpace="nowrap" weight="semibold">
              {i18n.t(
                'bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.table.employee_name'
              )}
            </Text.Normal>
          </Table.HeadCell>
          <Table.HeadCell>
            <Text.Normal size="200" whiteSpace="nowrap" weight="semibold">
              {i18n.t(
                'bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.table.missing_fields'
              )}
            </Text.Normal>
          </Table.HeadCell>
          <Table.HeadCell>
            <Text.Normal size="200" whiteSpace="nowrap" weight="semibold">
              {i18n.t(
                'bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.table.severity'
              )}
            </Text.Normal>
          </Table.HeadCell>
          <Table.HeadCell />
        </Table.Row>
      </Table.Head>
      <Table.BodyWithInfiniteScroll page={page} loadMore={loadMore} hasMore={hasMore} colSpan={4}>
        {paginatedItems.map((incidence) => (
          <IncidenceRow key={incidence.id} incidence={incidence} />
        ))}
      </Table.BodyWithInfiniteScroll>
    </Table>
  )
}

export default RuleEvaluationIncidenceTable

function incidenceRank(
  validity?: BookkeepersManagementValueObjectsIncidenceRuleValidityEnum
): number {
  switch (validity) {
    case 'blocked':
      return 0
    case 'warning':
      return 1
    default:
      return 2
  }
}

// Sort first by priority, then by employee name
function sortIncidences(
  incidenceOne: PayrollSendToBookkeeperIncidenceFragment,
  incidenceTwo: PayrollSendToBookkeeperIncidenceFragment
): number {
  const rankOne = incidenceRank(incidenceOne.ruleEvaluation?.validity)
  const rankTwo = incidenceRank(incidenceTwo.ruleEvaluation?.validity)
  if (rankOne !== rankTwo) {
    return rankOne - rankTwo
  }

  return (incidenceOne.employee?.fullName ?? '').localeCompare(
    incidenceTwo.employee?.fullName ?? ''
  )
}
